import React, { useState } from "react";
import { useFormik } from "formik";
import valid from "card-validator";
import {useNavigate} from "react-router-dom";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import StyledSelect from "@shared/components/Select";
import Input from "@shared/components/Input";
import HeartLoader from "@shared/HeartLoader";
import { secondaryModals, secondOverlayState, giftCheckoutState } from '@atoms';
import "./style.css";
import {paymentErrorsConfig} from "../../../helpers/PaymentErrors";
import useCardOrderPayment, { getCardExpDate } from "@helpers/hooks/useCardOrderPayment";
import {payGiftCardWithCard} from "../../../api/giftcard";

const AddCardFormMobile = () => {
  const [cardErr, setCardErr] = useState({
    cardNumberErr: false,
    cardHolderErr: false,
    cardExpMonErr: false,
    cardExpYearErr: false,
    cardCVVErr: false,
    cardIdErr: false,
  });
  const [isLoading, setLoading] = useState(false);
  const setModals = useSetRecoilState(secondaryModals);
  const [, setOverlay] = useRecoilState(secondOverlayState);

  // GIFT-CARD mode: one synchronous full-amount charge (regular J7 debit)
  // against the pre-created GC- intent — no card-check leg and no drawer
  // finalize step. The server charges its OWN computed amount.
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const navigate = useNavigate();

  // PHOTO orders: one press = J2 card check -> charge (only on 000) -> order
  // recorded -> success page. No second "אישור הזמנה" press in the drawer.
  const { payAndOrder, cancelPending, isPaying, paymentError: orderPaymentError } = useCardOrderPayment();
  const [paymentError, setPaymentError] = useState("");

  const getCurrentMonthString = () => {
    const month = new Date().getMonth()+1;
    if (month > 10) return `${month}`;
    return `0${month}`;
  }

  // Gift card: the server charges the intent's amount in one go and activates
  // the card synchronously — success routes straight to /gift-success.
  const handleGiftCardPayment = async (values) => {
    try {
      setLoading(true);
      setPaymentError("");
      const res = await payGiftCardWithCard({
        giftOrderId: giftCheckout.giftOrderId,
        card: {
          no: values.cardNumber.replace(/\s/g, ''),
          expdate: getCardExpDate(values.expiriedMonth, values.expiriedYear),
          cvv: values.cvv,
          id: values.id,
          email: giftCheckout.buyerEmail || "",
        },
      });

      if (!res.success) {
        if (res.errorCode in paymentErrorsConfig) {
          setPaymentError(paymentErrorsConfig[res.errorCode].error);
        } else {
          setPaymentError(res.error || "תקלה בכרטיס האשראי");
        }
        return;
      }

      handleCloseCardForm();
      localStorage.removeItem("orderId"); // the GC- watch id — never clear the whole storage here
      navigate(`/gift-success/${giftCheckout.giftOrderId}`);
    } catch (err) {
      console.log(err);
      setPaymentError("שגיאה בביצוע התשלום, נסו שוב");
    } finally {
      setLoading(false);
    }
  }

  const formik = useFormik({
    initialValues: {
      cardHolder: "",
      cardNumber: "",
      expiriedMonth: getCurrentMonthString(),
      expiriedYear: new Date().getFullYear(),
      cvv: "",
      id: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      setPaymentError("");
      const numberValidation = valid.number(values.cardNumber.replace(/s/g, '').replace(/_/g, '')).isPotentiallyValid && !!values.cardNumber;
      const holderValidation = valid.cardholderName(values.cardHolder).isPotentiallyValid && !!values.cardHolder;
      const expMonValidation = valid.expirationMonth(values.expiriedMonth).isPotentiallyValid && !!values.expiriedMonth;
      const expYearValidation = valid.expirationYear(values.expiriedYear.toString()).isPotentiallyValid && !!values.expiriedYear.toString();
      const cvvValidation = valid.cvv(values.cvv).isPotentiallyValid && !!values.cvv;
      if (!numberValidation || !holderValidation || !expMonValidation || !expYearValidation || !cvvValidation || !values.id) {
        setCardErr({
          cardNumberErr: !numberValidation,
          cardHolderErr: !holderValidation,
          cardExpMonErr: !expMonValidation,
          cardExpYearErr: !expYearValidation,
          cardCVVErr: !cvvValidation,
          cardIdErr: !values.id,
        })
        setLoading(false);
        return;
      }

      // Gift card: no card-check leg — one full J7 charge on the server-priced amount.
      if (giftMode) {
        await handleGiftCardPayment(values);
        return;
      }

      await payAndOrder(values, { beforeNavigate: handleCloseCardForm });
      setLoading(false);
    },
  });

  const monthArray = [...Array(12)].map((_,index) => {
    if (index >= 9) {
      return ({ value: `${index+1}`, label: `${index+1}` })
    }
    return ({ value: `0${index+1}`, label: `0${index+1}` })
  });

  const generateArrayOfYears = () => {
    const min = new Date().getFullYear()
    const max = min + 10
    const years = []

    for (var i = min; i <= max; i++) {
      years.push({ value: i, label: i })
    }
    return years
  }

  const handleCloseCardForm = () => {
    cancelPending();
    setModals(state => ({
      ...state,
      addCardMobile: { visible: false },
      selectCardMobile: { visible: false },
    }))
    setOverlay(false);
  }

  return (
    <form className="add-new-card-form-mobile" onSubmit={formik.handleSubmit}>
      <div className="add-new-card-form-mobile__header">
        <span onClick={() => handleCloseCardForm()}>&gt;</span>
        <span>תשלום מאובטח</span>
      </div>
      <div className="add-new-card-form-mobile__content">
        <div className="form__fields-mobile">
          <div className="form__fields-mobile--row no-bottom-radius">
            <div style={cardErr.cardHolderErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="cardHolder"
                onChange={formik.handleChange}
                value={formik.values.cardHolder}
                placeholder="שם בעל הכרטיס"
                style={{direction: "rtl"}}
              />
            </div>
          </div>
          <div className="form__fields-mobile--row no-top-radius" style={{marginTop: "-1px"}}>
            <div style={cardErr.cardNumberErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
              <Input
                name="cardNumber"
                mask=""
                type="tel"
                alwaysShowMask={false}
                onChange={(e) => {
                  let number = e.target.value.replace(/\s/g, '').replace(/_/g, '');
                  if (cardErr.cardNumberErr) {
                    setCardErr({...cardErr, cardNumberErr: false});
                  }
                  const isAmex = number.startsWith('34') || number.startsWith('37');
                  number = number.slice(0, isAmex ? 15 : 16);
                  e.target.value = isAmex
                    ? [number.slice(0, 4), number.slice(4, 10), number.slice(10, 15)].filter(Boolean).join(' ')
                    : (number.match(/.{1,4}/g) || []).join(' ');
                  formik.handleChange(e)}
                }
                placeholder="מספר כרטיס"
                value={formik.values.cardNumber}
                maxLength={19}
                style={{direction: "rtl"}}
                isFlexible={true}
              />
            </div>
          </div>
          <br />
          <div className="form__fields-mobile--row">
            <div className="cvv_content">
              <div className="cvv_content" style={cardErr.cardCVVErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
                <Input
                  name="cvv"
                  onChange={formik.handleChange}
                  value={formik.values.cvv}
                  mask=""
                  type="tel"
                  placeholder="קוד בטחון (CVV)"
                  maxLength={3}
                  style={{direction: "rtl"}}
                  isFlexible={true}
                />
                {/* <img
                  src="/assets/file/images/CVV_icon.png"
                  style={{position: "absolute", top: "50%", left: "15px", transform: "translate(0%, -50%)"}}
                /> */}
              </div>
            </div>
          </div>
          <br/>
          <div className="form__fields-mobile--row">
            <div className="cvv_content">
              <div className="cvv_content" style={cardErr.cardIdErr ? {border: "1px solid red", borderRadius: "6px"} : {}}>
                <Input
                  name="id"
                  type="tel"
                  onChange={formik.handleChange}
                  value={formik.values.id}
                  mask=""
                  placeholder="תעודת זהות של בעל הכרטיס"
                  style={{direction: "rtl"}}
                />
              </div>
            </div>
          </div>
          <div className="form__fields-mobile--row">
            <span style={{lineHeight: "36px", marginRight: "6px", marginBottom: "5px"}}>תוקף</span>
            <div className="selects">
              <div className="selects" style={(cardErr.cardExpMonErr || cardErr.cardExpYearErr) ? {border: "1px solid red", borderRadius: "6px"} : {}}>
                <StyledSelect
                  name="expiriedMonth"
                  options={monthArray}
                  placeholder="12"
                  onChange={(i) => formik.setFieldValue('expiriedMonth', i.value)}
                  defaultValue={monthArray.find(itm => itm.value === formik.values.expiriedMonth)}
                  isSearchable={false}
                />
                <StyledSelect
                  name="expiriedYear"
                  options={generateArrayOfYears()}
                  placeholder="2024"
                  onChange={(i) => formik.setFieldValue('expiriedYear', i.value)}
                  defaultValue={generateArrayOfYears().find(itm => itm.value === formik.values.expiriedYear)}
                  isSearchable={false}
                />
              </div>
            </div>
          </div>
          {(paymentError || orderPaymentError) && (
            <div className="form__fields-mobile--row" style={{color: "red", marginTop: "20px"}}>
              {paymentError || orderPaymentError}
            </div>
          )}
        </div>
      </div>
      <div className="add-new-card-form-mobile__footer">
        <button className="submit" type="submit" disabled={isLoading || isPaying}>
          {giftMode ? "אישור ותשלום" : "אישור הזמנה"}
        </button>
      </div>
      <div style={{display: "flex", padding: "20px", alignItems: "center", justifyContent: "space-between"}}>
        <div style={{width: "135px", height: "0px", border: "1px solid #CECECE"}}></div>
        <img src="/assets/file/images/mobile-card-footer-img.PNG" width="100%" alt=""/>
        <div style={{width: "135px", height: "0px", border: "1px solid #CECECE"}}></div>
      </div>
      <HeartLoader isLoading={isLoading || isPaying} showText={false}/>
    </form>
  );
};

export default AddCardFormMobile;
